<template>
  <b-dropdown
    id="dropdown-form"
    ref="dropdown"
    variant="link"
    size="sm"
    no-caret
    dropleft
    offset="-200"
    menu-class="dropdown-shadow"
    @hide="handlePreventDefault"
    @hidden="resetValues"
    @show="handleShow"
    v-click-outside="closeDropdown"
  >
    <template #button-content>
      <i style="font-size: smaller" class="fa fa-plus" aria-hidden="true"></i>
      <a style="font-size: smaller"> Add multiple patients</a>
    </template>
    <div class="d-flex justify-content-end">
      <b-icon-x
        class="hover-pointer mr-1 text-nowrap close-icon"
        @click="closeDropdown"
        >X</b-icon-x
      >
    </div>
    <div class="ml-4 mr-4 mt-3">
      <b-row>
        <b-col>
          <div class="mb-2">
            <p class="d-inline">LOC</p>
            <b-link
              v-on:click="locFilterClear"
              class="d-inline pull-right clear-link"
              >Clear</b-link
            >
          </div>
          <v-select
            :clearable="false"
            v-on:input="filterEocs"
            v-model="loc"
            :options="locs"
          />
        </b-col>
        <b-col>
          <div class="mb-2">
            <p class="d-inline">Status</p>
            <b-link
              @click="statusFilterClear"
              class="d-inline pull-right clear-link"
              >Clear</b-link
            >
          </div>
          <v-select
            :clearable="false"
            @input="filterEocs"
            v-model="status"
            :options="statuses"
          />
        </b-col>
      </b-row>
      <div class="mb-2 mt-3">
        <p class="d-inline">DOB</p>
        <b-link
          v-on:click="dateFilterClear"
          class="d-inline pull-right clear-link"
          >Clear</b-link
        >
      </div>
      <div class="mb-3">
        <b-row>
          <b-col>
            <p>From</p>
            <date-picker
              @input="filterEocs"
              v-model="startDateFilter"
            ></date-picker>
          </b-col>
          <b-col>
            <p>To</p>
            <date-picker
              @input="filterEocs"
              v-model="endDateFilter"
            ></date-picker>
          </b-col>
        </b-row>
      </div>
      <div class="pb-4 mt-3">
        <b-link v-on:click="selectAll" class="d-inline pull-right clear-link">{{
          allSelected ? "Clear Selection" : "Select All"
        }}</b-link>
        <b-form-input
          class="auth-input search-input border-color-light-gray"
          v-model="searchPhrase"
          placeholder="Search Patients..."
          :readonly="searchingPatient"
        />
      </div>
    </div>
    <b-list-group class="doc-list-group">
      <b-list-group-item
        :key="key"
        v-for="(pat, key) in eocs"
        href="#"
        class="flex-column align-items-start doc-list-item"
        @click="selectItem(pat.data)"
      >
        <div class="w-100">
          <b-container>
            <b-row>
              <b-col cols="1" align-self="center">
                <b-form-checkbox
                  id="checkbox-1"
                  :checked="selectedValues.find(v => v.id == pat.value) != null"
                  name="checkbox-1"
                  class="b-block"
                >
                </b-form-checkbox>
              </b-col>
              <b-col>
                <div class="d-flex flex-row justify-content-between">
                  <b-badge
                    :variant="statusVariant(pat.data.status)"
                    class="text-white mb-2"
                    >{{ statusText(pat.data.status) }}
                  </b-badge>
                </div>
                <div>
                  <span class="d-block"> {{ pat.label }}</span>
                  <span class="d-block">
                    DOB:
                    {{ formatDateOnly(pat.data.patient.dateOfBirth) }}</span
                  >
                  <span v-if="pat.data.levelOfCare" class="d-block">
                    Current LOC: {{ pat.data.levelOfCare.name }}</span
                  >
                </div>
              </b-col>
            </b-row>
          </b-container>
        </div>
      </b-list-group-item>
    </b-list-group>
    <div class="text-center mt-3 mb-3">
      <b-button
        @click="addPatients(selectedValues)"
        :variant="selectedValues.length > 0 ? 'primary' : 'secondary'"
        :disabled="selectedValues.length == 0"
        >Add {{ selectedValues.length }} patients</b-button
      >
    </div>
  </b-dropdown>
</template>

<script>
import serviceServer from "../../server/service";
import DatePicker from "./DatePicker";
import { mapGetters } from "vuex";
import eocServer from "../../server/eoc";
import { formatEndDateSql, formatStartDateSql } from "../../helpers/date";
import { validateDate } from "../../helpers/validation";

export default {
  name: "PatientBatchPicker",
  components: {
    DatePicker
  },
  props: ["value", "locationId"],
  data() {
    return {
      ageSelector: this.ageFilterInit(),
      eocs: [],
      locs: [],
      selectedValues: [],
      unfilteredEocs: [],
      loc: null,
      locSearch: null,
      status: null,
      startDateFilter: "",
      endDateFilter: "",
      allSelected: false,
      statuses: [
        {
          value: "Pending",
          label: "Pending"
        },
        {
          value: "Active",
          label: "Active"
        }
      ],
      isCloseable: false,
      preventDefault: true,
      searchPhrase: "",
      searchingPatient: false,
      searchAll: ""
    };
  },
  mounted() {
    this.fetchServices();
  },
  methods: {
    handlePreventDefault(event) {
      if (this.preventDefault) {
        event.preventDefault();
      }
    },
    handleShow() {
      this.preventDefault = true;
      this.filterEocs();
    },
    closeDropdown() {
      this.isCloseable = true;
      this.$refs.dropdown.hide();
      this.preventDefault = false;
    },
    async fetchServices() {
      const services = await serviceServer.query({
        type: "LevelOfCare"
      });
      this.locs = services
        .filter(levelOfCare =>
          this.levelsOfCareCurrentLocation
            .map(lc => lc.id)
            .includes(levelOfCare.id)
        )
        .map(service => ({
          value: service.id,
          label: `${
            service.billingCodes[0]
              ? service.billingCodes[0].cptCode + " - "
              : ""
          } ${service.name}`,
          data: service,
          costPerUnit: service.costPerUnit,
          numberOfUnits: 1
        }));
    },
    ageFilterInit() {
      let ageFilter = [];
      for (let i = 0; i < 101; i++) {
        if (i < 2) {
          ageFilter.push({ label: i + " year", code: i });
        } else {
          ageFilter.push({ label: i + " years", code: i });
        }
      }
      return ageFilter;
    },
    addPatients(selectedValues) {
      this.$emit("addPatients", selectedValues);
      this.$refs.dropdown.hide();
      this.resetValues();
      this.preventDefault = false;
    },
    selectItem(patient) {
      let selectedValues = this.selectedValues;
      let selectedValueIds = this.selectedValues.map(p => p.id);
      if (selectedValueIds.indexOf(patient.id) >= 0) {
        const index = selectedValueIds.indexOf(patient.id);
        selectedValues.splice(index, 1);
      } else {
        selectedValues.push(patient);
      }
      this.selectedValues = selectedValues;
    },
    selectAll() {
      if (!this.allSelected) {
        for (let index = 0; index < this.eocs.length; index++) {
          const eoc = this.eocs[index];
          const isNotSelected = !this.selectedValues.some(
            patient => patient.id === eoc.data.id
          );

          if (isNotSelected) this.selectedValues.push(eoc.data);
        }
        this.allSelected = true;
      } else {
        this.selectedValues = [];
        this.allSelected = false;
      }
    },
    async filterEocs() {
      let levelOfCareId = null;
      if (this.loc) {
        levelOfCareId = this.loc.data.id;
      }

      let status = null;
      if (this.status) {
        status = this.status.value;
      }

      let fromDate = null;
      if (this.startDateFilter && validateDate(this.startDateFilter)) {
        fromDate = this.startDateFilter;
      }

      let endDate = null;
      if (this.endDateFilter && validateDate(this.endDateFilter)) {
        endDate = this.endDateFilter;
      }

      const eocs = await eocServer.queryList(
        {
          searchPhrase: this.searchAll ? this.searchPhrase : "",
          eocStatus: this.searchAll
            ? this.searchAll
            : status
            ? [status]
            : ["Active", "Pending"],
          levelsOfCare: levelOfCareId ? [levelOfCareId] : [],
          fromDateOfBirth: fromDate ? formatStartDateSql(fromDate) : null,
          toDateOfBirth: endDate ? formatEndDateSql(endDate) : null,
          locations: this.locationId ? [this.locationId] : [],
          orderBy: this.searchAll && this.searchPhrase ? "status" : null,
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.eocs = eocs.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));

      if (this.value.length > 0) {
        for (let i = 0; i < this.value.length; i++) {
          let found = false;
          for (let j = 0; j < this.eocs.length && !found; j++) {
            if (this.eocs[j].value == this.value[i].value) {
              found = true;
              this.eocs.splice(j, 1);
            }
          }
        }
      }
    },
    locFilterClear() {
      this.loc = null;
      this.filterEocs();
    },
    statusFilterClear() {
      this.status = null;
      this.filterEocs();
    },
    dateFilterClear() {
      this.startDateFilter = "";
      this.endDateFilter = "";
      this.filterEocs();
    },
    statusVariant(status) {
      switch (status) {
        case "Pending":
          return "warning";
        case "Active":
          return "success";
        case "Inactive":
          return "danger";
        case "Draft":
          return "danger";
        default:
          return "";
      }
    },
    statusText(status) {
      return status;
    },
    resetValues() {
      this.selectedValues = [];
      this.startDateFilter = "";
      this.endDateFilter = "";
      this.eocs = [];
      this.loc = null;
      this.allSelected = false;
      this.status = null;
      this.isCloseable = false;
      this.searchPhrase = "";
    }
  },
  computed: {
    ...mapGetters("client", {
      levelsOfCareCurrentLocation: "levelsOfCareCurrentLocation"
    })
  },
  watch: {
    searchPhrase: function() {
      if (this.searchPhrase.length < 3) {
        this.searchAll = "";
      }
      if (this.searchPhrase.length >= 3) {
        this.searchAll = ["Active", "Pending", "Inactive"];
      }
      this.filterEocs();
    }
  }
};
</script>

<style scoped>
.doc-list-group {
  border-bottom: 0;
  margin-top: 10px;
  max-height: 400px;
  min-width: 500px;
  margin-bottom: 10px;
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
}
.doc-list-item {
  border-top: 0;
  border-left: 0;
  border-right: 0;
}
.select-filter {
  width: 30%;
  display: inline-block;
}
.close-icon {
  font-size: 20px;
}
</style>
